import React, { useEffect, useState } from 'react';
import { getSubscriberMcpAccess, getSubscriberRepos, selectSubscriberRepo, getGithubInstallUrl, type SubscriberMcpAccess } from '../api.js';
import { useAuth, type Subscriber } from '../context/AuthContext.js';

export default function McpAccessStatus() {
  const { user, subscriber, subscriberResolved, subscriberFetchFailed } = useAuth();
  if (!user) return <section className="card" aria-label="MCP access"><p role="status">Sign in to check MCP access.</p></section>;
  const subscriberStatus = subscriberResolved && !subscriberFetchFailed && subscriber?.id === user.id ? subscriber.status : null;
  // Remount on account change: a prior account's result must never be displayed.
  return <McpAccessState key={user.id} subscriberStatus={subscriberStatus} />;
}

function McpAccessState({ subscriberStatus }: { subscriberStatus: Subscriber['status'] | null }) {
  const [state, setState] = useState<SubscriberMcpAccess | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Auto-select a lone granted repo once; a still-provisioning result after that
  // must not re-trigger setup in a loop.
  const [autoSelect, setAutoSelect] = useState(true);
  // The repo the subscriber chose survives the status refresh that follows it.
  const [chosen, setChosen] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setState(null);
    setFailed(false);
    void getSubscriberMcpAccess().then((result: SubscriberMcpAccess): void => {
      if (active) setState(result);
    }).catch((): void => { if (active) setFailed(true); });
    return (): void => { active = false; };
  }, [attempt]);
  const label = failed ? 'MCP access state unavailable'
    : !state ? 'Checking MCP access state...'
      : state.provisioning_status === 'ready' ? 'MCP access ready'
        : state.provisioning_status === 'blocked' ? 'MCP access blocked' : 'MCP access provisioning';
  const blockedGuidance = subscriberStatus === 'cancelled'
    ? 'Your subscription is cancelled. Review your plan in account settings.'
    : subscriberStatus === 'past_due'
      ? 'Your subscription needs a payment update. Review your billing details in account settings.'
      : 'MCP access is unavailable for this account. Review your access eligibility or contact support.';
  return (
    <section className="card" aria-label="MCP access" style={{ padding: 18, margin: 0 }}>
      <h3 style={{ marginTop: 0, marginBottom: 6 }}>MCP access</h3>
      <p role="status" style={{ margin: '0 0 4px' }}>{label}</p>
      {state && <p style={{ margin: '0 0 8px' }}>Account created. {state.provisioning_status === 'ready'
        ? 'Workspace setup is complete. Connections still require sign-in, consent, and an eligible plan.'
        : state.provisioning_status === 'blocked'
          ? blockedGuidance
          : 'Workspace setup is still in progress.'}</p>}
      {state?.provisioning_status === 'provisioning' && (
        <RepoSetup
          autoSelect={autoSelect}
          chosen={chosen}
          note={setupNote}
          onDone={(repo: string, note: string | null): void => {
            setAutoSelect(false);
            setChosen(repo);
            setSetupNote(note);
            setAttempt((current: number): number => current + 1);
          }}
        />
      )}
      <button type="button" onClick={(): void => setAttempt((current: number): number => current + 1)}>
        {failed ? 'Retry' : 'Refresh status'}
      </button>
    </section>
  );
}

const REASON_TEXT: Record<string, string> = {
  awaiting_repo: 'Choose a repository to finish setup.',
  grant_not_active: 'Your workspace access is not active yet. We have been alerted.',
  mc_registration_or_readiness_failed: 'Our setup service did not respond. Try again in a minute.',
  readiness_finalize_failed: 'Setup did not finish. Try again in a minute; we have been alerted if it keeps failing.',
  platform_repo_forbidden: 'That repository cannot be used. Pick one of your own repositories.',
};

function noteFor(status: string, reason: string | null | undefined): string | null {
  if (status === 'ready') return null;
  if (reason && REASON_TEXT[reason]) return REASON_TEXT[reason];
  return 'Setup is still finishing. Refresh in a minute.';
}

/** Workspace setup needs one subscriber repo. Lists the repos granted to the GitHub
 * App and finishes setup on selection (auto-selects when exactly one is granted). */
function RepoSetup({ autoSelect, chosen, note, onDone }: {
  autoSelect: boolean;
  chosen: string | null;
  note: string | null;
  onDone: (repo: string, note: string | null) => void;
}) {
  const [repos, setRepos] = useState<string[] | null>(null);
  const [choice, setChoice] = useState(chosen ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(note);
  const [installUrl, setInstallUrl] = useState<string | null>(null);

  const finish = async (repo: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await selectSubscriberRepo(repo);
      try { window.dispatchEvent(new Event('ocn:projects-changed')); } catch { /* non-browser */ }
      onDone(repo, noteFor(result.provisioning_status, result.reason));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not finish setup. Try again.');
      setBusy(false);
    }
  };

  useEffect(() => {
    let active = true;
    void getSubscriberRepos().then((result): void => {
      if (!active) return;
      setRepos(result.repos);
      // Never preselect: a default the subscriber did not choose gets submitted by accident.
      setChoice((current: string): string => (current && result.repos.includes(current) ? current : ''));
      if (result.repos.length === 1 && autoSelect) void finish(result.repos[0]);
      if (result.repos.length === 0) {
        void getGithubInstallUrl().then((r): void => { if (active) setInstallUrl(r.url); })
          .catch((): void => {
            if (active) setError('GitHub setup is unavailable. Open Config to connect GitHub for your project, then refresh status.');
          });
      }
    }).catch((): void => { if (active) setRepos([]); });
    return (): void => { active = false; };
  }, []);

  if (repos === null) return <p>Checking your GitHub connection...</p>;
  if (repos.length === 0) {
    return (
      <div style={{ margin: '8px 0' }}>
        <p style={{ margin: '0 0 8px' }}>Connect GitHub so we can work on your code.</p>
        {installUrl && <a className="btn btn-primary" href={installUrl}>Install the OCNexus GitHub App</a>}
        {error && <>
          <p role="alert" style={{ margin: '6px 0' }}>{error}</p>
          <a className="btn btn-primary" href="/?page=config">Open GitHub project settings</a>
        </>}
      </div>
    );
  }
  if (repos.length === 1 && autoSelect) return <p>{busy ? `Setting up your workspace on ${repos[0]}...` : error ?? `Workspace repository: ${repos[0]}`}</p>;
  return (
    <div style={{ margin: '8px 0' }}>
      <label htmlFor="ocn-primary-repo">Choose your main repository</label>
      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <select id="ocn-primary-repo" value={choice} disabled={busy}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => setChoice(e.target.value)}>
          <option value="" disabled>Select a repository</option>
          {repos.map((r: string) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="button" className="btn btn-primary" disabled={busy || !choice} onClick={(): void => { void finish(choice); }}>
          {busy ? 'Setting up...' : 'Finish setup'}
        </button>
      </div>
      {error && <p role="alert" style={{ margin: '6px 0 0' }}>{error}</p>}
    </div>
  );
}
